import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError } from "@/api/client";
import { useUsers } from "@/api/queries/users";
import { useSetConnectorOwner } from "@/api/mutations/connectors";

// #279/E-3: Fachverantwortlichen bestimmen, übergeben oder entfernen.
//
// Warum das mehr ist als ein Feld: erst ein gesetzter Verantwortlicher erzeugt die
// Antragspflicht aus #279. Ohne diesen Dialog war der ganze Antrags-Ablauf in der Praxis
// nicht erreichbar — man konnte die Anträge sehen, aber den Zustand nicht herstellen, in dem
// sie entstehen.

function ownerErrorMessage(err: unknown): string {
  const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : undefined;
  switch (code) {
    case "OWNER_CLEAR_FORBIDDEN":
      return "Den Verantwortlichen entfernen darf nur ein Administrator — übergeben Sie stattdessen an eine Person.";
    case "APPROVAL_FORBIDDEN":
      return "Keine Berechtigung für diesen Connector.";
    case "INVALID_CONNECTOR_CONFIG":
      return "Diese Person existiert nicht (mehr).";
    default:
      return err instanceof Error ? err.message : "Aktion fehlgeschlagen";
  }
}

export function SetConnectorOwnerDialog({
  connectorId,
  currentOwnerId,
  canClear,
  open,
  onOpenChange,
}: {
  connectorId: string;
  currentOwnerId: string | null;
  /** Nur ein Admin darf ownerlos machen — sonst wird die Option gar nicht angeboten. */
  canClear: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const users = useUsers();
  const setOwner = useSetConnectorOwner();
  const [selected, setSelected] = useState<string>(currentOwnerId ?? "");

  const submit = async (userId: string | null): Promise<void> => {
    try {
      await setOwner.mutateAsync({ id: connectorId, userId });
      onOpenChange(false);
      toast.success(userId ? "Verantwortlicher gesetzt." : "Verantwortlicher entfernt.");
    } catch (err) {
      toast.error(ownerErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verantwortlichen festlegen</DialogTitle>
          <DialogDescription>
            Der Fachverantwortliche entscheidet, welche Agenten diesen Connector nutzen dürfen. Ohne
            Verantwortlichen gehört er dem System — dann aktivieren die Agent-Besitzer ihn selbst.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="owner-select">Person</Label>
          <select
            id="owner-select"
            className="h-9 w-full rounded-md border border-border bg-surface px-2 text-[13px]"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="owner-select"
          >
            <option value="">— niemand ausgewählt —</option>
            {(users.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
                {u.id === currentOwnerId ? " (aktuell)" : ""}
              </option>
            ))}
          </select>
          {users.isError && (
            <p className="text-[12px] text-danger">Die Personen-Liste konnte nicht geladen werden.</p>
          )}
        </div>

        <DialogFooter>
          {currentOwnerId && canClear && (
            <Button
              variant="ghost"
              onClick={() => void submit(null)}
              disabled={setOwner.isPending}
              data-testid="owner-clear"
            >
              Entfernen (dem System zuordnen)
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void submit(selected)}
            disabled={setOwner.isPending || !selected || selected === currentOwnerId}
            data-testid="owner-save"
          >
            {currentOwnerId ? "Übergeben" : "Festlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
